import {Component, OnDestroy, OnInit} from '@angular/core';
import {MatTableDataSource} from "@angular/material/table";
import {PathwayEntry} from '@tva/ng-migration/cre-tracking/models';
import {StudentCreTrackingService} from "@tva/ng-migration/student/components/student-cre-tracking/student-cre-tracking.service";
import {OverrideModalComponent} from "@tva/ng-migration/student/components/student-cre-tracking/student-override-modal/student-override-modal.component";
import {MatDialog} from "@angular/material/dialog";
import {FormArray} from "@angular/forms";
import {CreSummaryService} from "@tva/ng-migration/student/services/cre-summary.service";
import {Student} from "@tva/ng-migration/student/models/student.model";
import {AuthenticationService} from "@tva/ng-migration/core";
import {NotificationService} from "@tva/ng-migration/uicomponents";
import {UnsavedChangesService} from "@tva/ng-migration/shared/services/unsavedChanges.service";
import {RootScopeService} from "@tva/ng-migration/core/services/rootscope/rootscope.service";
import {LocationService} from "@tva/ng-migration/core/services/location/location.service";
import {StateService} from "@tva/ng-migration/core/services/state/state.service";
import {StudentCreTrackingConstants} from "./student-cre-tracking.constants";

@Component({
  selector: 'app-student-cre-tracking',
  templateUrl: './student-cre-tracking.component.html',
  styleUrls: ['./student-cre-tracking.component.scss'],
  providers: [StudentCreTrackingService],
})

export class StudentCreTrackingComponent implements OnInit, OnDestroy {
  pathwayName;
  dataSource: MatTableDataSource<any>;
  dataSourceSummary: MatTableDataSource<any>;
  dataSourceCompleted: MatTableDataSource<any>;
  pathwayRequirements;
  selectedPathwayEntries = [];
  displayColumns = ["subject", "grade", "status", "notes", "action"];
  displaySummaryColumns = ["pathway", "status", "completed", "remaining"];
  displayCompletedColumns = ["pathway", "school", "date"];
  data: any;
  pathwayStatus: any;
  pathwaySummary: any;
  pathwayCompleted: any[];
  student: Student;
  wasModified:boolean;

  pathwayCoursesDataForm: FormArray = new FormArray([]);
  pathwayCoursesData: PathwayEntry[] = [];

  MODAL_MESSAGE =  'You have made changes to the student\'s selected pathway. Do you want to proceed without saving these changes?';
  promptAsking;

  constructor(
    private service: StudentCreTrackingService,
    private dialog: MatDialog,
    private creSummary: CreSummaryService,
    private authentication: AuthenticationService,
    private notificationService: NotificationService,
    private unsavedChanges: UnsavedChangesService,
    private rootScope: RootScopeService,
    private location: LocationService,
    private state: StateService
  ) {
  }

  ngOnInit(): void {
    this.dataSource = new MatTableDataSource(this.selectedPathwayEntries);
    this.dataSourceSummary = new MatTableDataSource(this.pathwaySummary);
    this.dataSourceCompleted = new MatTableDataSource(this.pathwayCompleted);
    this.loadPathwayRequirements();
    this.unsavedChanges.setMessage(this.MODAL_MESSAGE);

    this.promptAsking = this.rootScope.onChangeStart( (event, next) => {
      event.preventDefault();
      this.unsavedChanges.verify(()=> {
        this.promptAsking();
        this.state.go(next.name)
        if(next.url.includes('student-details')){
          document.getElementsByClassName("studentDetailsModal")[0]['style'].display = 'block';
        } else {
          document.getElementsByClassName("studentContainer")[0]['style'].display = 'block';
        }
      })
    });
  }

  ngOnDestroy(){
    this.unsavedChanges.saveChanges();
    this.unsavedChanges.resetMessage();
    this.promptAsking();
  }


  loadPathwayRequirements(): void {
    this.service.registerAfterSearch((pathwayRequirements, pathwayName, pathwayStatus, selectedPathwayEntries, pathwaySummary, pathwayCompleted) => {
      this.pathwayRequirements = pathwayRequirements;
      this.pathwayName = pathwayName;
      this.pathwayStatus = pathwayStatus;
      this.selectedPathwayEntries = selectedPathwayEntries;
      this.pathwaySummary = pathwaySummary;
      this.pathwayCompleted = pathwayCompleted;
      this.updateDataSource();
    });
    this.service.registerStudentSearchUpdate();
  }

  updateDataSource(): void {
    this.dataSource.data = this.selectedPathwayEntries;
    this.dataSourceSummary.data = this.pathwaySummary;
    this.dataSourceCompleted.data = this.pathwayCompleted;
  }

  calcRows(course: any): number {
    const currentRequirement = this.pathwayRequirements.find(requirement => requirement.requiredCourses.includes(course));
    if (currentRequirement.requiredCourses.length && currentRequirement.requiredCourses[0] === course) {
      return currentRequirement.requiredCourses.length;
    } else {
      return 0;
    }
  }

  cellIsHidden(course): boolean {
    const rows = this.calcRows(course);
    return rows === 0;
  }

  getNotes(course): string {
    let notes = "";
    const currentRequirement = this.pathwayRequirements.find(requirement => requirement.requiredCourses.includes(course));
    const override = currentRequirement.requiredCourses.find(course => course.courseOverride);

    if (override) {
      notes = `Fulfilled by ${override.courseOverride.courseName}`
    } else {
      const rows = this.calcRows(course);
      if (rows > 1) {
        notes = "Either course fulfills\n this requirement.";
      }
    }
    return notes;
  }

  getStatus(course): string {
    return this.pathwayRequirements.find(requirement => requirement.requiredCourses.includes(course)).completionStatusId;
  }


  getGradeClass(pathwayEntry): string {
    if (pathwayEntry.courseOverride || pathwayEntry.completionStatusId == 3) {
      return 'bgGreen'
    } else if (pathwayEntry.completionStatusId == 1) {
      return 'bgGrey'
    } else if (pathwayEntry.completionStatusId == 2) {
      return 'bgBlue'
    } else if (pathwayEntry.completionStatusId == 4) {
      return 'bgRed'
    }
  }

  pathwaysOverrideCourse(entry: any) {
    const requirement = this.pathwayRequirements.find(requirement => requirement.requiredCourses.includes(entry));

    this.creSummary.getOverrideCourses(this.service.student.studentId, this.service.student.schoolId, requirement.id).subscribe((response) => {
      const {requiredCourses, transcriptCourses} = response;

      const data = {
        requirement: {...requirement},
        overrideCourses: requiredCourses,
        transcriptCourses: transcriptCourses,
      }

      this.dialog.open(OverrideModalComponent, {width: '800px', data}).afterClosed().subscribe(updatedRequirement => {
        if (updatedRequirement) {
          const index = this.pathwayRequirements.findIndex(requirement => requirement.id === updatedRequirement.id);
          this.pathwayRequirements[index] = updatedRequirement;
          this.updateDataSource();
          this.wasModified = true;
          this.unsavedChanges.startChanges();
          document.getElementsByClassName("studentContainer")[0]['style'].display = 'none';
          document.getElementsByClassName("studentDetailsModal")[0]['style'].display = 'none';
        }
      })
    });

  }

  isOverride(): boolean{
    return (this.pathwayStatus == 0 || this.pathwayStatus == 1 || this.pathwayStatus == 2) && this.authentication.hasPathwayRequirementEditRole()
  }

  save(): void{
    const overrides = [];
    this.pathwayRequirements.forEach((currentRequirement)=>{
      const overriddenCourse = currentRequirement.requiredCourses.find(course => course.courseOverride);
      if (overriddenCourse)
        overrides.push({
              "pathwayRequirementId": currentRequirement.id,
              "completionStatusId": currentRequirement.completionStatusId,
              "requiredCourseId": overriddenCourse.id,
              "tranCourseId": overriddenCourse.courseOverride.tranCourseId
        });
      else
        overrides.push({
          "pathwayRequirementId": currentRequirement.id,
          "completionStatusId": currentRequirement.completionStatusId
        });
    });
    this.creSummary.updateOverrideCourses(this.service.student.studentId, this.service.student.schoolId, overrides).subscribe(()=>{
      this.notificationService.notifySuccess();
      this.reset();
    }, ()=>{
      this.notificationService.notifyError();
    })

  }

  reset(): void{
    this.service.loadData();
    this.wasModified = false;
    this.unsavedChanges.saveChanges();
  }

  getPathwayStatusText(course): string {
    const selectedRequirement = this.pathwayRequirements.find(requirement => requirement.requiredCourses.includes(course));
    const status_list = [];

    if (selectedRequirement.requiredCourses.some(item => item.courseOverride))
      return StudentCreTrackingConstants.PATHWAY_STATUS.COMPLETED

    selectedRequirement.requiredCourses.forEach(({completionStatusId}) => status_list.push(completionStatusId));

    if (status_list.includes(StudentCreTrackingConstants.PATHWAY_STATUS_ID.COMPLETED))
      return StudentCreTrackingConstants.PATHWAY_STATUS.COMPLETED

    if (status_list.includes(StudentCreTrackingConstants.PATHWAY_STATUS_ID.IN_PROGRESS))
      return StudentCreTrackingConstants.PATHWAY_STATUS.IN_PROGRESS

    if (status_list.includes(StudentCreTrackingConstants.PATHWAY_STATUS_ID.NOT_STARTED))
      return StudentCreTrackingConstants.PATHWAY_STATUS.NOT_STARTED

    if (status_list.includes(StudentCreTrackingConstants.PATHWAY_STATUS_ID.FAILED))
      return StudentCreTrackingConstants.PATHWAY_STATUS.FAILED

    return StudentCreTrackingConstants.PATHWAY_STATUS.OUTSTANDING;
  }

  getAllPathwaysCompletionStatusText(completionStatusId: number) {
    return StudentCreTrackingConstants.ALL_PATHWAY_COMPLETION_STATUS_LIST[completionStatusId];
  }
}
